import React, { useState, useEffect, useMemo } from 'react';
import ReactWordcloud from 'react-wordcloud';
import { enterpriseDashboardAPI } from '../services/api';
import styles from '../styles/OrganizationDashboard.module.css';

/**
 * Organization Dashboard Component
 * Displays enterprise analytics for organization admins
 *
 * Props:
 * - organizationId: ID of the organization to display
 * - organizationName: Name of the organization
 * - onBack: Callback to navigate back
 */
function OrganizationDashboard({ organizationId, organizationName, onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  // Memoize wordcloud options to prevent infinite re-renders
  const wordcloudOptions = useMemo(() => ({
    rotations: 2,
    rotationAngles: [-90, 0],
    fontSizes: [16, 60],
    colors: ['#2d6a4f', '#40916c', '#52b788', '#74c69d', '#95d5b2'],
    enableTooltip: true,
    deterministic: true,
    fontFamily: 'impact',
    fontStyle: 'normal',
    fontWeight: 'normal',
    padding: 2,
    scale: 'sqrt',
    spiral: 'archimedean',
    transitionDuration: 1000,
  }), []);

  const wordcloudCallbacks = useMemo(() => ({
    getWordTooltip: (word) => `${word.text}: ${word.value} occurrences (${word.userCount || 0} members)`,
  }), []);

  useEffect(() => {
    async function fetchDashboard() {
      try {
        setLoading(true);
        setError(null);

        const data = await enterpriseDashboardAPI.getDashboard(organizationId);
        setDashboardData(data);
      } catch (err) {
        console.error('[OrgDashboard] Error fetching dashboard:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    if (organizationId) {
      fetchDashboard();
    }
  }, [organizationId]);

  if (loading) {
    return (
      <div className={styles.container}>
        <button onClick={onBack} className={styles.backButton}>← Back</button>
        <div className={styles.loading}>Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <button onClick={onBack} className={styles.backButton}>← Back</button>
        <div className={styles.error}>
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { overview, behaviorFunnel, employeeProgress, wordCloud, behaviorHeatmap } = dashboardData;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button onClick={onBack} className={styles.backButton}>← Back</button>
        <h1 className={styles.title}>{organizationName} Dashboard</h1>
      </div>

      {/* Overview Stats */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Overview</h2>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{overview.total_enrolled}</div>
            <div className={styles.statLabel}>Total Enrolled</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{overview.active_7d}</div>
            <div className={styles.statLabel}>Active (7 days)</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{overview.active_30d}</div>
            <div className={styles.statLabel}>Active (30 days)</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{overview.avg_tasks_completed ? Number(overview.avg_tasks_completed).toFixed(1) : '0.0'}</div>
            <div className={styles.statLabel}>Avg Tasks Completed</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{overview.avg_days_active ? Number(overview.avg_days_active).toFixed(1) : '0.0'}</div>
            <div className={styles.statLabel}>Avg Days Active</div>
          </div>
        </div>
      </section>

      {/* Behavior Funnel */}
      {behaviorFunnel && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Engagement Funnel</h2>
          <div className={styles.funnel}>
            <div className={styles.funnelStep}>
              <div className={styles.funnelBar} style={{ width: '100%' }}>
                <span className={styles.funnelLabel}>Enrolled: {behaviorFunnel.enrolled}</span>
              </div>
            </div>
            <div className={styles.funnelStep}>
              <div className={styles.funnelBar} style={{ width: `${(behaviorFunnel.active / behaviorFunnel.enrolled) * 100}%` }}>
                <span className={styles.funnelLabel}>Active: {behaviorFunnel.active}</span>
              </div>
            </div>
            <div className={styles.funnelStep}>
              <div className={styles.funnelBar} style={{ width: `${(behaviorFunnel.completed_tasks / behaviorFunnel.enrolled) * 100}%` }}>
                <span className={styles.funnelLabel}>Completed Tasks: {behaviorFunnel.completed_tasks}</span>
              </div>
            </div>
            {behaviorFunnel.coach_session !== undefined && (
              <div className={styles.funnelStep}>
                <div className={styles.funnelBar} style={{ width: `${(behaviorFunnel.coach_session / behaviorFunnel.enrolled) * 100}%` }}>
                  <span className={styles.funnelLabel}>Coach Session: {behaviorFunnel.coach_session}</span>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Completed Tasks Word Cloud */}
      {wordCloud && wordCloud.words && wordCloud.words.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>What Are Your Members Working On?</h2>
          <div className={styles.wordCloudContainer}>
            <ReactWordcloud
              words={wordCloud.words}
              options={wordcloudOptions}
              callbacks={wordcloudCallbacks}
            />
          </div>
        </section>
      )}

      {/* Behavioural Patterns Heatmap */}
      {behaviorHeatmap && behaviorHeatmap.tagMetrics && behaviorHeatmap.tagMetrics.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Behavioural Patterns</h2>
            <p className={styles.sectionSubtitle}>
              Conversation tag distribution over the last {behaviorHeatmap.timeRange}
            </p>
          </div>
          <div className={styles.heatmapContainer}>
            <table className={styles.heatmapTable}>
              <thead>
                <tr>
                  <th>Tag Type</th>
                  <th>Unique Users</th>
                  <th>Total Instances</th>
                  <th>Median Value</th>
                  <th>Distribution</th>
                </tr>
              </thead>
              <tbody>
                {behaviorHeatmap.tagMetrics.map((metric) => (
                  <tr key={metric.tag_type}>
                    <td className={styles.tagTypeCell}>
                      <span className={styles.tagTypeBadge}>{metric.tag_type}</span>
                    </td>
                    <td className={styles.metricCell}>{metric.unique_users}</td>
                    <td className={styles.metricCell}>{metric.total_instances}</td>
                    <td className={styles.metricCell}>
                      {metric.median_value ? metric.median_value.toFixed(1) : 'N/A'}
                    </td>
                    <td className={styles.distributionCell}>
                      {metric.display_type === 'quantifiable' ? (
                        <>
                          <div className={styles.distributionBar}>
                            {metric.high_count > 0 && (
                              <div
                                className={styles.distributionSegment}
                                style={{
                                  width: `${(metric.high_count / metric.total_instances) * 100}%`,
                                  backgroundColor: '#2d6a4f'
                                }}
                                title={`High: ${metric.high_count}`}
                              />
                            )}
                            {metric.medium_count > 0 && (
                              <div
                                className={styles.distributionSegment}
                                style={{
                                  width: `${(metric.medium_count / metric.total_instances) * 100}%`,
                                  backgroundColor: '#74c69d'
                                }}
                                title={`Medium: ${metric.medium_count}`}
                              />
                            )}
                            {metric.low_count > 0 && (
                              <div
                                className={styles.distributionSegment}
                                style={{
                                  width: `${(metric.low_count / metric.total_instances) * 100}%`,
                                  backgroundColor: '#d8f3dc'
                                }}
                                title={`Low: ${metric.low_count}`}
                              />
                            )}
                          </div>
                          <div className={styles.distributionLegend}>
                            <span>High: {metric.high_count}</span>
                            <span>Med: {metric.medium_count}</span>
                            <span>Low: {metric.low_count}</span>
                          </div>
                        </>
                      ) : (
                        <div className={styles.qualitativeValues}>
                          {metric.top_values && metric.top_values.map((value, idx) => (
                            <span key={idx} className={styles.valueBadge}>{value}</span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Member Progress */}
      {employeeProgress && employeeProgress.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Member Progress (Anonymized)</h2>
          <p className={styles.privacy}>Member identities are hashed for privacy. Data is aggregated and anonymized.</p>
          <div className={styles.table}>
            <table>
              <thead>
                <tr>
                  <th>Member ID</th>
                  <th>Tasks Completed</th>
                  <th>Days Active</th>
                  <th>Trend</th>
                  <th>Last Active</th>
                </tr>
              </thead>
              <tbody>
                {employeeProgress.map((emp, idx) => (
                  <tr key={idx}>
                    <td className={styles.hashedId}>{emp.employee_id?.substring(0, 8)}...</td>
                    <td>{emp.tasks_completed}</td>
                    <td>{emp.days_active}</td>
                    <td>
                      <span className={`${styles.trend} ${styles[emp.engagement_trend]}`}>
                        {emp.engagement_trend}
                      </span>
                    </td>
                    <td>{emp.last_active ? new Date(emp.last_active).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

export default OrganizationDashboard;
